import { useQuery } from '@tanstack/react-query';

import authAxios from '../../components/authAxios';

/* Single seam for the customer overview's loan performance data.

   Live: GET loans/customers/{id}/dashboard — current loan case plus the
   lifetime KPI aggregates. Keys are returned exactly as the API spells
   them so there is no mapper to keep in sync; CustomerView reads them
   directly.

   Still placeholder: `riskGrade`. Grading has no endpoint yet, so it
   keeps its "Coming soon" tag.

   `currency` comes from the ['clientProfile'] query, not this endpoint.

   Decimal fields arrive as strings ("1249.96"). They are passed through
   untouched — CustomerView's formatAmount does the number formatting. */

const LOANS_MENU_CODE = 'LOANS';
const LOANS_MENU_NAME = 'Loans';

/* The loans module is gated by /saas/user-menu/ and nothing else: a client
   without it simply gets no Loans node back, which is why the Sidebar has
   no separate flag to read. Walk the same tree the Sidebar renders, and
   match the way its MenuIcon does — code first, then name. */
const hasLoansMenu = (items = []) =>
    items.some((item) =>
        item.code === LOANS_MENU_CODE
        || item.name === LOANS_MENU_NAME
        || hasLoansMenu(item.children));

const useCustomerLoanSummary = (customerId, { enabled = true } = {}) => {
    const { data: clientProfile } = useQuery({
        queryKey: ['clientProfile'],
        queryFn: async () => {
            const response = await authAxios.get(`/clientprofiles/${localStorage.getItem('clientProfile')}`);
            return response.data;
        },
    });

    // Same key and request as the Sidebar's menu query, so this reads that
    // cache rather than issuing a second call.
    const { data: userMenu } = useQuery({
        queryKey: ['userMenu'],
        queryFn: async () => {
            const response = await authAxios.get('/saas/user-menu/');
            return response.data;
        },
    });

    // undefined = menu not back yet, so "loans off" is never assumed from a
    // cold cache; false = this client genuinely has no loans module.
    const loansEnabled = userMenu ? hasLoansMenu(userMenu) : undefined;

    const wanted = enabled && !!customerId;

    const dashboard = useQuery({
        queryKey: ['customer-loan-dashboard', customerId],
        queryFn: async () => {
            const response = await authAxios.get(`loans/customers/${customerId}/dashboard`);
            return response.data;
        },
        enabled: wanted && loansEnabled === true,
    });

    return {
        // Nulls in the payload are meaningful (no live loan, nothing due
        // yet, no repayment ever posted) — CustomerView renders each as its
        // own state rather than as a zero.
        ...(dashboard.data || {}),

        currency: clientProfile?.currency_code || '',

        // False only once the menu has actually come back without a Loans
        // node — CustomerView drops both loan blocks in that case rather than
        // rendering empty figures for a client that has no loans module.
        loansEnabled,

        // No endpoint yet — still placeholder, still tagged in the UI.
        riskGrade: 'LOW',
        isRiskPlaceholder: true,

        // isInitialLoading, not isLoading: a disabled query sits in the
        // 'loading' status forever, which would pin the skeletons on.
        isLoading: dashboard.isInitialLoading || (wanted && loansEnabled === undefined),
        isError: dashboard.isError,
    };
};

export default useCustomerLoanSummary;
